import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Salary } from '@xing-com/crate-jobs-components-salary';
import type {
  JobKeyfactV2Fragment,
  JobMatchingHighlightsV2Fragment,
  JobTeaserData,
} from '@xing-com/crate-jobs-graphql';
import { selectJobData } from '@xing-com/crate-jobs-helpers';
import { useAppliedTheme } from '@xing-com/hub';
import { mediaWideNavless } from '@xing-com/layout-tokens';
import { Marker } from '@xing-com/marker';
import { scale110, scale160, spaceXS } from '@xing-com/tokens';

import { FactMarker } from './fact-marker';

export const MarkerContainer = styled.div<{
  $isCompacted: boolean;
  $applyButtonVisible: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  gap: ${spaceXS};
  padding-right: ${({ $applyButtonVisible }) =>
    $applyButtonVisible ? `${scale160}` : 0};
  ${({ $isCompacted }) => {
    const compactedStyles = css`
      max-height: ${scale110};
      overflow: hidden;

      @media ${mediaWideNavless} {
        max-height: ${scale160};
      }
    `;
    return $isCompacted ? compactedStyles : '';
  }}

  &:empty {
    display: none;
  }
`;

const FatMarker = styled(Marker)`
  & > * {
    font-weight: bold;
  }
`;

type Props = {
  isCompacted?: boolean;
  job: JobTeaserData;
  matchingHighlights: JobMatchingHighlightsV2Fragment | null | undefined;
  showEmploymentType?: boolean;
  applyButtonVisible?: boolean;
};

const hasEmploymentTypeFact = (facts: JobKeyfactV2Fragment[]): boolean =>
  facts.findIndex(
    (fact) =>
      fact.__typename === 'JobMatchingHighlightsJobKeyfactStringV2' &&
      fact.type === 'job_keyfact_employment_type'
  ) !== -1;

const hasSalaryFact = (facts: JobKeyfactV2Fragment[]): boolean =>
  facts.findIndex(
    (fact) => fact.__typename === 'JobMatchingHighlightsJobKeyfactSalaryV2'
  ) !== -1;

export const JobTeaserFacts: React.FC<Props> = ({
  isCompacted = false,
  job,
  matchingHighlights,
  showEmploymentType,
  applyButtonVisible = false,
}) => {
  const isDarkMode = useAppliedTheme() === 'dark';
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const markerContainerRef = useRef<HTMLDivElement | null>(null);

  const { salary, showSalary, employmentTypeValue } = selectJobData(job);

  const isTakenJob = job.__typename === 'TakenJob';

  const includesEmploymentTypeFact =
    matchingHighlights &&
    (hasEmploymentTypeFact(matchingHighlights.matchingFacts) ||
      hasEmploymentTypeFact(matchingHighlights.nonMatchingFacts));

  const showEmploymentTypeFromJob =
    showEmploymentType && employmentTypeValue && !includesEmploymentTypeFact;

  const includesSalaryFact =
    matchingHighlights &&
    (hasSalaryFact(matchingHighlights.matchingFacts) ||
      hasSalaryFact(matchingHighlights.nonMatchingFacts));

  const showSalaryFromJob = salary && showSalary && !includesSalaryFact;

  const takenJobMarker = isTakenJob ? (
    <FatMarker
      key="taken-job"
      variant={isDarkMode ? 'onColor' : 'onColorStrong'}
    >
      <FormattedMessage id={'DREAM_MACHINE_JOB_CARD_TYPE_OCCUPIED_COMPACT'} />
    </FatMarker>
  ) : null;

  const matchingFactsCount = matchingHighlights?.matchingFacts.length ?? 0;
  const nonMatchingFactsCount =
    matchingHighlights?.nonMatchingFacts.length ?? 0;

  const matchingFacts = matchingHighlights
    ? matchingHighlights.matchingFacts.map((fact, i) => (
        <FactMarker key={'matching-' + i} variant="match" fact={fact} />
      ))
    : [];

  const nonMatchingFacts = matchingHighlights
    ? matchingHighlights.nonMatchingFacts.map((fact, i) => (
        <FactMarker key={'nonMatching-' + i} variant="default" fact={fact} />
      ))
    : [];

  const employmentTypeFromJob = showEmploymentTypeFromJob ? (
    <Marker key="employmentType" variant="default">
      {employmentTypeValue}
    </Marker>
  ) : null;

  const salaryFromJob = showSalaryFromJob ? (
    <Marker key="salary" variant="default">
      <Salary salary={salary} />
    </Marker>
  ) : null;

  const allMarkers = [
    takenJobMarker,
    ...nonMatchingFacts,
    ...matchingFacts,
    employmentTypeFromJob,
    salaryFromJob,
  ].filter((marker) => !!marker);

  useLayoutEffect(() => {
    if (visibleCount !== null) {
      return;
    }

    const element = markerContainerRef.current;
    if (!element) {
      return;
    }

    const maxHeight = element.clientHeight;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const children = Array.from(element.children) as HTMLElement[];
    const visible = children.findIndex(
      (child) => child.offsetTop + child.offsetHeight > maxHeight + 1
    );

    setVisibleCount(visible === -1 ? children.length : visible);
  }, [visibleCount, allMarkers.length]);

  useLayoutEffect(() => {
    setVisibleCount(null);
  }, [
    matchingFactsCount,
    nonMatchingFactsCount,
    showEmploymentTypeFromJob,
    showSalaryFromJob,
    isTakenJob,
  ]);

  useEffect(() => {
    const handleResize = (): void => {
      setVisibleCount(null);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderMarkers = (count: number): React.JSX.Element[] =>
    isCompacted ? allMarkers.slice(0, count) : allMarkers;

  return (
    <MarkerContainer
      ref={markerContainerRef}
      aria-hidden={visibleCount === null}
      role="presentation"
      $isCompacted={isCompacted}
      $applyButtonVisible={applyButtonVisible}
    >
      {visibleCount === null ? allMarkers : renderMarkers(visibleCount)}
    </MarkerContainer>
  );
};
